import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { CheckoutFormSchema } from '../schema';
import type { ICheckoutAddress, IPaymentMethod, IShippingMethod } from '../types';
import { openDpdWidget } from './dpd-pickup-point';
import Method from './Method';

interface IProps {
  shippingMethods: IShippingMethod[];
  paymentMethods: IPaymentMethod[];
  weight: number;
  dpdApiKey?: string;
}

export default function StepMethods({
  paymentMethods,
  shippingMethods,
  weight,
  dpdApiKey,
}: IProps) {
  const { t } = useTranslation();

  const { control, setValue } = useFormContext<CheckoutFormSchema>();

  const [showDpdPickupPoints, setShowDpdPickupPoints] = useState(false);

  const shippingId = useWatch({ control, name: 'shippingMethodId' });
  const paymentId = useWatch({ control, name: 'paymentMethodId' });
  const isCompany = useWatch({ control, name: 'customer.isCompany' });
  const customer = useWatch({ control, name: 'customer' });
  const dpdPickupPoint = useWatch({ control, name: 'dpdPickupPoint' });

  const shippingAddress = useMemo<Omit<ICheckoutAddress, 'addressId'> | null>(() => {
    const { shippingAddress, billingStreet, billingTown, billingPostCode, billingCountryCode } =
      customer;
    if (shippingAddress) {
      const { street, town, postCode, countryCode } = shippingAddress;
      if (street && town && postCode && countryCode) return { street, town, postCode, countryCode };
    }

    if (
      billingStreet.trim() === '' ||
      billingTown.trim() === '' ||
      billingPostCode.trim() === '' ||
      billingCountryCode.trim() === ''
    ) {
      return null;
    }

    return {
      street: billingStreet,
      town: billingTown,
      postCode: billingPostCode,
      countryCode: billingCountryCode,
    } satisfies Omit<ICheckoutAddress, 'addressId'>;
  }, [customer]);

  const allowedShippingMethods = useMemo(
    () =>
      shippingMethods
        .filter(sm => (isCompany ? sm.companyPayments : sm.citizenPayments).length > 0)
        .map(sm => sm.id),
    [shippingMethods, isCompany]
  );
  const allowedPayments = useMemo(() => {
    const shippingMethod = shippingMethods.find(t => t.id === shippingId);
    const paymentIds = isCompany
      ? shippingMethod?.companyPayments
      : shippingMethod?.citizenPayments;
    return paymentIds || [];
  }, [shippingMethods, shippingId, isCompany]);

  useEffect(() => {
    if (!showDpdPickupPoints) return;
    openDpdWidget(
      {
        weight: weight,
        postCode: shippingAddress!.postCode,
        countryCode: shippingAddress!.countryCode,
      },
      dpdApiKey!
    )
      .then(data => {
        setValue('dpdPickupPoint', data);
        setValue('paymentMethodId', '');
        setShowDpdPickupPoints(false);
      })
      .catch(err => {
        setValue('shippingMethodId', '');
        setValue('paymentMethodId', '');
        setValue('dpdPickupPoint', null);
        setShowDpdPickupPoints(false);
        console.log(err);
      });
  }, [showDpdPickupPoints]);

  // Ak bude potrebne, aby sa odznacili podmienky, pri nastaveni/ostaveni dodacej adresy
  // useEffect(() => {
  //   setValue('shippingMethodId', '');
  //   setValue('paymentMethodId', '');
  // }, [isDifferentShipping, setValue]);

  return (
    <div className="methods">
      <div className="shipping">
        <h4>{t('ShippingMethod')}</h4>

        <div className="form-fields">
          {shippingMethods.map(sm => (
            <Method
              disabled={!shippingAddress}
              key={sm.id}
              {...sm}
              isAllowed={allowedShippingMethods.includes(sm.id)}
              selectedMethodId={shippingId}
              onClick={() => {
                if (!sm.isDpdPickupPoint) return;
              }}
              onSelect={() => {
                setValue('dpdPickupPoint', null);
                setShowDpdPickupPoints(sm.isDpdPickupPoint);
              }}
            />
          ))}
        </div>
      </div>

      <div className="payment">
        <h4>{t('PaymentMethod')}</h4>

        <div className="form-fields">
          {paymentMethods.map(pm => (
            <Method
              disabled={!shippingAddress}
              key={pm.id}
              {...pm}
              isAllowed={allowedPayments.includes(pm.id)}
              selectedMethodId={paymentId}
            />
          ))}
        </div>
      </div>

      {!showDpdPickupPoints && dpdPickupPoint && (
        <div className="mt-4 shadow p-4 shadow-primary">
          <h3>Odberné miesto</h3>
          <div className="grid grid-cols-[minmax(4rem,min-content)_auto] gap-x-4 gap-y-2">
            <span className="whitespace-nowrap">Názov odberného miesta</span>
            <span>{dpdPickupPoint.name}</span>
            <span className="whitespace-nowrap">Ulica</span>
            <span>{dpdPickupPoint.street}</span>
            <span className="whitespace-nowrap">PSČ + Mesto</span>
            <span>
              {dpdPickupPoint.postCode} {dpdPickupPoint.town}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
